import { useState } from 'react'
import { Button, ErrorBanner, Field, Modal, TextInput } from '../../components/forms'
import { useAbandonSale, useParkSale, useParkedSales, useResumeSale, type CartLine, type ParkedSale } from '../../features/pos/api'

/** A stored instant as a time of day, which is how a counter talks about one. */
function clock(at: string): string {
  return new Date(at).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', hour12: false })
}

/** Pieces, without a trailing ".00" on a whole number. */
function quantity(value: number): string {
  return value.toLocaleString('en-KE', { maximumFractionDigits: 2 })
}

/** The time of day a hold runs out, which is what a cashier actually tells the customer. */
function heldUntil(hours: number): string {
  const at = new Date(Date.now() + hours * 60 * 60 * 1000)
  return at.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', hour12: false })
}

/**
 * Parking a sale and picking it up again (D-197).
 *
 * **The basket outlives its stock, on purpose.** A parked sale can be picked up for three days; the
 * stock behind it goes back on the shelf within hours, because a basket somebody forgot would
 * otherwise keep the last of something off sale all week. The list says which is which, so a cashier
 * knows before they press whether the goods are still put by.
 */
export function ParkButton({
  locationId, registerSessionId, customerId, cart, onParked, holdHours = 6, holdChoices,
}: {
  locationId: string
  registerSessionId: string
  customerId: string
  cart: CartLine[]
  onParked: () => void
  /** The shop's own default, from the till's context (D-212). */
  holdHours?: number
  /** What the server will accept. Absent on an older API, where the default is the only choice. */
  holdChoices?: number[]
}) {
  const park = useParkSale(locationId)
  const [open, setOpen] = useState(false)
  const [reference, setReference] = useState('')
  const [hours, setHours] = useState(holdHours)

  const choices = holdChoices ?? [holdHours]

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!reference.trim()) return

    await park.mutateAsync({
      registerSessionId,
      customerId,
      reference: reference.trim(),
      stockHoldHours: hours,
      lines: cart.map((l) => ({
        productId: l.productId,
        quantity: l.quantity,
        discountPercent: l.discountPercent,
        discountAmount: l.discountAmount,
        // The unit is parked with the line (D-210): the hold the server moves is in pieces, so a
        // box parked as a single would put the rest of the box back on the shelf.
        asPack: l.asPack === true,
      })),
    })

    setReference('')
    setHours(holdHours)
    setOpen(false)
    onParked()
  }

  return (
    <>
      <Button tone="secondary" disabled={cart.length === 0} onClick={() => setOpen(true)}>
        Park this sale
      </Button>

      <Modal open={open} title="Park this sale" onClose={() => setOpen(false)}>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <ErrorBanner error={park.error} />

          <p>
            The counter is freed for the next customer. {cart.length} line{cart.length === 1 ? '' : 's'} are kept
            for three days whatever happens to the stock.
          </p>

          <Field
            label="What shall we call it?"
            htmlFor="park-reference"
            hint="A name, a phone number, anything you will recognise"
          >
            <TextInput
              id="park-reference"
              autoFocus
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Mary — blue shirt"
            />
          </Field>

          {/* D-212: how long the goods stay off the shelf, decided by whoever is talking to the
              customer. Three choices and not a box to type in: a basket somebody forgot must not be
              able to keep the last of something off sale for two days. */}
          <fieldset>
            <legend className="mb-1 block text-xs font-medium text-dpl-muted">Hold the stock for</legend>

            <div role="radiogroup" aria-label="Hold the stock for" className="flex flex-wrap gap-2">
              {choices.map((choice) => (
                <button
                  key={choice}
                  type="button"
                  role="radio"
                  aria-checked={hours === choice}
                  onClick={() => setHours(choice)}
                  className={`rounded-md border px-4 py-2 text-sm font-medium transition ${
                    hours === choice
                      ? 'border-dpl-primary bg-dpl-primary text-white'
                      : 'border-dpl-border bg-dpl-surface hover:border-dpl-primary/40'
                  }`}
                >
                  {choice} hours
                </button>
              ))}
            </div>

            <p className="mt-1 text-xs text-dpl-muted">
              The goods go back on the shelf at about {heldUntil(hours)}
              {hours === holdHours && ' — this shop\u2019s usual'}. The sale itself stays in the list either way.
            </p>
          </fieldset>

          <Button type="submit" busy={park.isPending} disabled={!reference.trim()}>Park it</Button>
        </form>
      </Modal>
    </>
  )
}

export function ParkedSalesButton({
  locationId, registerSessionId, busyWithASale, onResume,
}: {
  locationId: string
  registerSessionId: string
  /** There is a sale on the counter. Picking another up would replace it, and take its hold with it. */
  busyWithASale: boolean
  onResume: (sale: ParkedSale) => void
}) {
  const parked = useParkedSales(locationId)
  const resume = useResumeSale(locationId)
  const abandon = useAbandonSale(locationId)
  const [open, setOpen] = useState(false)

  const waiting = parked.data ?? []

  return (
    <>
      <Button tone="ghost" onClick={() => setOpen(true)}>
        Parked sales{waiting.length > 0 && ` (${waiting.length})`}
      </Button>

      <Modal open={open} title="Parked sales" onClose={() => setOpen(false)} wide>
        <ErrorBanner error={resume.error ?? abandon.error} />

        {busyWithASale && waiting.length > 0 && (
          <p className="mb-3 rounded-lg bg-dpl-bg px-3 py-2 text-sm">
            There is a sale on the counter. Finish it or park it first — picking one of these up would
            replace it.
          </p>
        )}

        {waiting.length === 0 ? (
          <p className="text-sm text-dpl-muted">Nothing is parked at this shop.</p>
        ) : (
          <ul className="space-y-2">
            {waiting.map((sale) => (
              <li
                key={sale.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dpl-border p-3 text-sm"
              >
                <span className="min-w-0">
                  <span className="block font-medium">{sale.reference}</span>
                  <span className="block text-xs text-dpl-muted">
                    {sale.customer.name} · {sale.lineCount} line{sale.lineCount === 1 ? '' : 's'} · parked by {sale.parkedBy}
                    {sale.stockStillHeld && sale.heldForHours > 0 && ` · held ${sale.heldForHours}h, until ${clock(sale.stockHeldUntil)}`}
                  </span>

                  {/* Said before they press, not after. A cashier who knows the stock went can warn
                      the customer instead of discovering it mid-sale. */}
                  {!sale.stockStillHeld && (
                    <span className="mt-0.5 block text-xs font-medium text-dpl-warning">
                      The stock is back on the shelf — we will check it again
                    </span>
                  )}

                  {/* D-212: and WHICH of it is no longer there. "We will check it again" tells a
                      cashier to worry; this tells them what to say to the customer. A sale still
                      holding its own stock is never short of it — the server adds its own hold back
                      before comparing. */}
                  {sale.isShort && (
                    <span className="mt-0.5 block text-xs font-medium text-dpl-danger">
                      {sale.lines
                        .filter((l) => l.shortfall > 0)
                        .map((l) =>
                          l.availableNow <= 0
                            ? `${l.name}: none left`
                            : `${l.name}: only ${quantity(l.availableNow)} of ${quantity(l.pieces)}`)
                        .join(' · ')}
                    </span>
                  )}
                </span>

                <span className="flex shrink-0 gap-2">
                  <Button
                    busy={resume.isPending}
                    disabled={busyWithASale}
                    onClick={async () => {
                      await resume.mutateAsync({ id: sale.id, registerSessionId })
                      onResume(sale)
                      setOpen(false)
                    }}
                  >
                    Pick it up
                  </Button>

                  {/* Asked, because it is somebody's shopping and there is no undo: the lines go
                      and the stock goes back on the shelf. */}
                  <Button
                    tone="ghost"
                    busy={abandon.isPending}
                    onClick={() => {
                      if (window.confirm(`Drop “${sale.reference}”? The ${sale.lineCount} line${sale.lineCount === 1 ? '' : 's'} go and the stock goes back on the shelf.`)) {
                        abandon.mutate(sale.id)
                      }
                    }}
                  >
                    Drop it
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </>
  )
}
